/**
 * Client utilities for the form-snapshot feature (issue #4064 / parent #3373).
 *
 * captureSnapshot(filler) -> static HTML of the rendered form body.
 * uploadSnapshot(qrId, html) -> POSTs to the account-scoped backend; returns the new docref id.
 * resolveSnapshotUrls(docRefId) -> reads the DocumentReference and returns broker URLs.
 * resolveSnapshotDocRefs({taskId, subjectId}) -> walks task -> group QRs -> snapshot docref id(s).
 * fetchGroupQrs(groupTaskToken) -> full QR resources for a group session.
 *
 * Capture is gated by BnQuestionnaireFiller's isSnapshotsEnabled flag; this module
 * doesn't enforce the gate itself.
 */

import { format } from 'date-fns';
import { sanitizeHtml } from '@/common/config';
import { $http } from '@/common/api/http.service';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { useAuthStore } from '@/stores/auth';
import { captureSnapshot as captureStaticHtml } from '@/common/print';
import timezoneApi from '@/common/api/timezone.api.js';
import { ensureFormSnapshots } from '@/common/snapshotCapture';

/**
 * Capture a self-contained static HTML snapshot of the form filler's rendered body.
 *
 * Prefers filler.getSnapshotHtml() — an async method that drives BnFormRenderPrint
 * with captureOnly=true and returns a fully-rendered, script-free HTML string. Falls
 * back to filler.getCaptureRoot() (cloneNode on the live filler DOM) for callers that
 * haven't been migrated. Reserved-region markers (data-signer-placeholder,
 * data-signer-task-id, data-appendages, data-appendage-index) survive both paths
 * because the underlying transform doesn't strip data-* attributes.
 *
 * Always emits a <section data-appendages> wrapper — if the rendered DOM didn't
 * include one (e.g. the filler had no appendages section yet), we append an
 * empty placeholder so sub-issue C has a stable mount point.
 *
 * @param {{ getSnapshotHtml?: () => Promise<string>, getCaptureRoot?: () => HTMLElement }} filler
 * @returns {Promise<{ html: string }>}
 */
export async function captureSnapshot(filler) {
    let html;
    if (typeof filler?.getSnapshotHtml === 'function') {
        html = await filler.getSnapshotHtml();
    } else {
        const root = typeof filler?.getCaptureRoot === 'function' ? filler.getCaptureRoot() : null;
        html = captureStaticHtml(root);
    }
    if (typeof html !== 'string') html = '';

    // Reserved-region invariant: at least one <section data-appendages> must be present.
    if (!/<section[^>]*data-appendages/i.test(html)) {
        html += '<section class="bn-appendages" data-appendages></section>';
    }

    return { html };
}

// DocumentReference.category systems a snapshot carries: its group-note kind ('combined' or
// 'participant') and, for one not captured at lock, how it was captured ('after-lock').
export const SNAPSHOT_KIND_SYSTEM = 'http://fhir.bestnotes.com/r4/snapshot-kind';
export const SNAPSHOT_CAPTURE_SYSTEM = 'http://fhir.bestnotes.com/r4/snapshot-capture';

/**
 * Read the QR id off a DocumentReference's context.related.
 *
 * Aidbox normalizes References to { id, resourceType, display } on read, but the
 * snapshot is written with the FHIR { reference: "QuestionnaireResponse/..." } string
 * form. Handle both so the resolver is robust to either normalization.
 *
 * @param {object} docRef raw DocumentReference (R4 context.related shape)
 * @returns {string|null}
 */
export function resolveQrId(docRef) {
    const related = docRef?.context?.related?.[0];
    if (!related) return null;
    if (related.resourceType === 'QuestionnaireResponse' && related.id) {
        return related.id;
    }
    if (typeof related.reference === 'string' && related.reference.startsWith('QuestionnaireResponse/')) {
        return related.reference.slice('QuestionnaireResponse/'.length);
    }
    return null;
}

// The conflicts that mean there is nothing to write: someone captured the form first
// (snapshot_exists), the stored snapshot needs no repair (snapshot_current), or it is a legacy
// combined group snapshot, which a repair must not narrow (snapshot_combined).
const SETTLED_CONFLICTS = ['snapshot_exists', 'snapshot_current', 'snapshot_combined'];

/**
 * POST the captured HTML to the account-scoped snapshot endpoint.
 * Throws on 4xx/5xx — the caller is responsible for surfacing the error.
 *
 * @param {string} questionnaireResponseId
 * @param {string} html
 * @param {('combined'|'participant'|null)} [kind] Group-note snapshot kind; tags the
 *   DocumentReference so the resolver can distinguish the combined doc from a
 *   per-participant one. Omitted for non-group single forms.
 * @param {{ afterLock?: boolean, repair?: boolean }} [options] afterLock marks a snapshot captured
 *   when a completed form is opened rather than at lock. The server refuses one for a response that
 *   already has a snapshot (409 snapshot_exists), which resolves to that existing snapshot.
 *   repair replaces a stored snapshot the broken review renderer produced (#6762) in place; the
 *   server re-checks the stored copy and refuses one that is not stale (409 snapshot_current) or
 *   that is a legacy combined group snapshot (409 snapshot_combined); each resolves to that
 *   snapshot. repair wins over afterLock. Errors on either path are left to the caller rather than
 *   shown as a snackbar.
 * @returns {Promise<{ documentReferenceId: string, settled?: string }>} settled names the 409 code
 *   when the server wrote nothing and pointed at the snapshot it already holds.
 */
export async function uploadSnapshot(questionnaireResponseId, html, kind = null, { afterLock = false, repair = false } = {}) {
    const authStore = useAuthStore();
    const url = `/api/account/${authStore.account.id}/questionnaire-response/${questionnaireResponseId}/snapshot`;
    const body = kind ? { html, kind } : { html };
    const capture = repair ? 'repair' : afterLock ? 'after-lock' : null;
    if (!capture) {
        const response = await $http.post(url, body);
        return { documentReferenceId: response?.data?.documentReferenceId };
    }
    try {
        const response = await $http.post(url, { ...body, capture }, { errorHandle: false, skipLogging: true });
        return { documentReferenceId: response?.data?.documentReferenceId };
    } catch (error) {
        const data = error?.response?.data;
        if (error?.response?.status === 409 && SETTLED_CONFLICTS.includes(data?.code)) return { documentReferenceId: data.documentReferenceId, settled: data.code };
        throw error;
    }
}

/**
 * The signers/reviewers on a QR that still owe a signature or review (no `when`). Used by
 * the snapshot dialog to drive the signature-reconciliation picker (#4393). Each returned
 * entry is the raw QR.reviewer entry (who, type, role, when, credentials, jobTitle).
 *
 * @param {object} questionnaireResponse Raw QR resource (must include reviewer[])
 * @returns {object[]}
 */
export function getPendingSigners(questionnaireResponse) {
    if (!Array.isArray(questionnaireResponse?.reviewer)) return [];
    return questionnaireResponse.reviewer.filter((r) => !r?.when && (r?.type === 'sign' || r?.type === 'review'));
}

/**
 * Build the signed (or reviewed) signature-block fragment that replaces a
 * [data-signer-placeholder] in the stored snapshot. Mirrors the signed-state markup of
 * BnFormSignatureBlock (classes carry the inlined snapshot CSS) but is self-contained so
 * the dialog can produce it without the live Vue renderer. The data-signer-task-id must
 * match the placeholder's value (the signer's who.id) so the backend splice can locate it.
 *
 * The signer's identity fields are read directly off the passed reviewer entry (do NOT
 * spread it first — the entry is a fhirworks model whose `who` is a non-enumerable
 * getter, so a spread silently drops who.id/display and the fragment would carry an empty
 * data-signer-task-id). Credential/jobTitle overrides come in through opts instead.
 *
 * @param {object} signer The reviewer entry being fulfilled (who, type, role, jobTitle, credentials)
 * @param {{ when?: string, signatureImage?: string, credentials?: string, jobTitle?: string, display?: string }} [opts]
 *   when = ISO timestamp; signatureImage = base64 data URL (canvas); credentials/jobTitle/display override the signer's
 * @returns {string}
 */
export function buildSignedSignatureBlock(
    signer,
    { when = new Date().toISOString(), signatureImage = '', credentials: credentialsOverride, jobTitle: jobTitleOverride, display: displayOverride } = {},
) {
    const authStore = useAuthStore();
    const taskIdValue = signer?.taskId || signer?.who?.id || '';
    const isReview = signer?.type === 'review';
    const display = displayOverride || signer?.who?.display || signer?.who?.fullName || '';
    const rawCredentials = credentialsOverride ?? signer?.credentials;
    const credentials = rawCredentials ? `, ${rawCredentials}` : '';
    const jobTitle = jobTitleOverride ?? signer?.jobTitle ?? '';
    const dateStr = when ? format(new Date(when), authStore.outputDateFormat) : '';
    const timeStr = when ? format(new Date(when), 'h:mm a') : '';
    const tz = when ? timezoneApi.getLocalTimeZoneAbbreviation(when) : '';
    const role = signer?.role || '';
    const legend = isReview ? 'Reviewed' : 'Electronically signed';

    // Patient identity (name + signature image) carries the usprivacy redaction hook,
    // matching BnFormSignatureBlock's live render so the reconcile-spliced fragment masks
    // the same elements as the rest of the snapshot.
    const privacyClass = signer?.who?.resourceType === 'Patient' ? ' class="usprivacy"' : '';

    const dot = '<span class="sig-dot">·</span>';
    const nameLine =
        `<b${privacyClass}>${escapeHtml(display)}</b>` +
        (credentials ? `<span class="esig-cred">${escapeHtml(credentials)}</span>` : '') +
        (jobTitle ? dot + escapeHtml(jobTitle) : '') +
        (dateStr || timeStr ? dot + `<span class="esig-when">${escapeHtml([dateStr, timeStr, tz].filter(Boolean).join(' '))}</span>` : '');
    const imageLine = signatureImage ? `<div class="esig-sig"><img${privacyClass} src="${escapeHtml(signatureImage)}" alt="" /></div>` : '';

    // One element, as the API's fragment validation requires, carrying the task id it claims
    // to sign. The shape is the locked signature block so a signature spliced in after lock
    // reads as one of the signatures already on the document rather than a patch.
    return (
        `<div class="esig bn-signature-signed" data-signer-task-id="${escapeHtml(taskIdValue)}">` +
        '<div class="esig-hd">' +
        `<span class="esig-role">${escapeHtml(role)}</span>` +
        '</div>' +
        // The status rides with the name, as BnFormSignatureBlock renders it — a spliced
        // signature has to match the ones already on the document, chip and all.
        `<div class="esig-name"><span class="esig-who">${nameLine}</span>` +
        `<span class="esig-legend">${legend}</span></div>` +
        imageLine +
        '</div>'
    );
}

/**
 * Escape a string for safe interpolation as HTML text content. Used for the
 * plain-text appendage metadata (author label, timestamp) so markup there can't
 * subvert the server-side fragment validation or the snapshot's legal-record
 * integrity. The note body itself is rich text and is sanitized rather than
 * escaped — see buildAppendageArticle.
 *
 * @param {string} value
 * @returns {string}
 */
export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Build the <article> markup for an appended addendum, matching the structure
 * BnFormRender emits for a locked/print appendage (no delete button — the snapshot is
 * immutable). The server appends this fragment into the snapshot's
 * <section data-appendages> region.
 *
 * The note body is rich-text HTML from BnRichTextEditor, so it's sanitized (matching
 * BnFormRender's `v-html="$sanitizeHtml(...)"` render) rather than escaped — escaping
 * would surface the markup as literal HTML in the snapshot.
 *
 * @param {{ text: string, authorDisplay?: string, timestamp?: string, index: number }} appendage
 * @returns {string}
 */
export function buildAppendageArticle({ text, authorDisplay = '', timestamp = '', index }) {
    const authStore = useAuthStore();
    const when = timestamp ? format(new Date(timestamp), authStore.outputDateFormat + ' h:mm a') : '';
    const author = authorDisplay || 'Unknown';
    const label = when ? `${author}, ${when}` : author;
    return (
        `<article class="bn-form-appendages-item d-flex" data-appendage-index="${index}" data-appendage-timestamp="${escapeHtml(timestamp)}">` +
        '<div class="flex-grow-1">' +
        `<div class="flex-grow-1 bn-form-appendages-item-label">Appended by: ${escapeHtml(label)}</div>` +
        `<div>${sanitizeHtml(text)}</div>` +
        '</div>' +
        '</article>'
    );
}

/**
 * Post-lock mutation: replace the pending-signer placeholder in a stored snapshot with
 * the freshly rendered signed-signature fragment. Throws on 4xx/5xx.
 *
 * @param {string} documentReferenceId
 * @param {string} taskId Signer task id matching the [data-signer-placeholder]
 * @param {string} fragmentHtml The rendered .bn-signature-signed[data-signer-task-id] element
 * @returns {Promise<{ replaced: boolean }>}
 */
export async function applySignatureToSnapshot(documentReferenceId, taskId, fragmentHtml) {
    const authStore = useAuthStore();
    const url = `/api/account/${authStore.account.id}/form-snapshots/${documentReferenceId}/signature`;
    const response = await $http.post(url, { taskId, fragmentHtml });
    return { replaced: !!response?.data?.replaced };
}

/**
 * Post-lock mutation: append an addendum <article> to a stored snapshot's appendages
 * region. Throws on 4xx/5xx.
 *
 * @param {string} documentReferenceId
 * @param {string} fragmentHtml The rendered <article data-appendage-index> element
 * @returns {Promise<{ appendageIndex: number }>}
 */
export async function appendToSnapshot(documentReferenceId, fragmentHtml) {
    const authStore = useAuthStore();
    const url = `/api/account/${authStore.account.id}/form-snapshots/${documentReferenceId}/appendage`;
    const response = await $http.post(url, { fragmentHtml });
    return { appendageIndex: response?.data?.appendageIndex ?? 0 };
}

/**
 * Read the DocumentReference and return broker URLs for each available format.
 * pdfUrl is null until sub-issue B lands the PDF generation pipeline.
 *
 * @param {string} documentReferenceId
 * @returns {Promise<{ htmlUrl: string, pdfUrl: string | null }>}
 */
export async function resolveSnapshotUrls(documentReferenceId) {
    const response = await $httpFhirApi.get(`/DocumentReference/${documentReferenceId}`);
    const docRef = response?.data || {};
    const content = Array.isArray(docRef.content) ? docRef.content : [];

    const htmlEntry = content.find((c) => c?.attachment?.contentType === 'text/html');
    const pdfEntry = content.find((c) => c?.attachment?.contentType === 'application/pdf');

    return {
        htmlUrl: htmlEntry?.attachment?.url || null,
        pdfUrl: pdfEntry?.attachment?.url || null,
        // The raw DocumentReference, which still carries context.related (the parsed instance
        // drops it), so a caller that also needs the response it points at need not read it again.
        docRef: response?.originalData || null,
    };
}

/**
 * Fetch the stored HTML bytes via the authenticated broker endpoint. Used by
 * BnFormSnapshotView to feed the iframe via `srcdoc` rather than `src` — that
 * way the iframe doesn't depend on the broker URL resolving against the page
 * origin (the relative path stored on the DocumentReference resolves against
 * the Vite dev server in dev, not the API), and we can sandbox the iframe to
 * an opaque origin without needing `allow-same-origin`.
 *
 * @param {string} htmlUrl - broker URL from resolveSnapshotUrls
 * @returns {Promise<string>}
 */
export async function fetchSnapshotHtml(htmlUrl) {
    if (!htmlUrl) return '';
    const response = await $http.get(htmlUrl, { responseType: 'text', transformResponse: [(v) => v] });
    return typeof response?.data === 'string' ? response.data : '';
}

/**
 * Fetch the full set of QRs for a group note, given the group's `groupTask` token
 * (an extension on Task, not a parent id). Collects QR ids the same way
 * BnQuestionnaireFiller loads a group session: the canonical Group QR's task isn't a
 * direct `groupTask=` match and only surfaces through the same include/revinclude
 * graph, so mirror that query for parity. QRs are loaded by id, NOT by encounter — in
 * a group session the QRs live on per-participant form encounters, not the appointment
 * encounter the task points at, so an encounter-scoped QR search comes back empty.
 * transformFhirResponse flattens primary + included resources into `data`, so QR ids
 * are harvested from both included QuestionnaireResponses and every task focus; the
 * `_id` fetch then loads each fully (formPdfSnapshot is a first-class extension that
 * _elements projection can strip, and unlock needs whole resources anyway).
 *
 * @param {string} parentId - the group's groupTask token
 * @param {string[]} [seedQrIds] - QR ids to include regardless of the group query
 * @returns {Promise<object[]>} full QuestionnaireResponse resources
 */
export async function fetchGroupQrs(parentId, seedQrIds = []) {
    const includeGraph =
        '&_include=Task:subject:Patient,Task:encounter:Encounter,Task:based-on:Appointment,Appointment:bnAppointmentType:BN_EncounterType,Encounter:subject,Task:owner:Practitioner' +
        '&_revinclude=Task:part-of,Task:encounter:Encounter,EpisodeOfCare:patient:Patient,Coverage:beneficiary:Patient,Encounter:part-of:Encounter' +
        '&_include=Task:focus:QuestionnaireResponse';
    const qrIds = new Set(seedQrIds.filter(Boolean));
    const groupResp = await $httpFhirApi.get(`Task?groupTask=${parentId}${includeGraph}`).catch(() => null);
    const groupResources = Array.isArray(groupResp?.data) ? groupResp.data : [];
    groupResources.forEach((resource) => {
        if (resource?.resourceType === 'QuestionnaireResponse' && resource.id) qrIds.add(resource.id);
        else if (resource?.resourceType === 'Task' && resource.focus?.id) qrIds.add(resource.focus.id);
    });
    if (!qrIds.size) return [];
    const qrsResp = await $httpFhirApi.get(`QuestionnaireResponse?_id=${[...qrIds].join(',')}`).catch(() => null);
    return Array.isArray(qrsResp?.data) ? qrsResp.data : [];
}

const unwrapSingle = (resp) => {
    const d = resp?.data;
    if (!d) return null;
    return Array.isArray(d) ? d[0] || null : d;
};

/**
 * Resolve the snapshot DocumentReference id(s) to display for a task: from a task id
 * (parent group task OR child participant task), walk to the group's QRs and pick the
 * right snapshot(s). Logs each step's outcome so failures are diagnosable in dev tools
 * without a redeploy.
 *
 * Group notes have no Group QR and no dedicated combined snapshot — every participant
 * QR (including QR[0], which also owns the group section) carries its OWN snapshot
 * (group section + that one participant). So the result is a list:
 *   - With a `subjectId` (participant-chart / activity-log entry): a single-element list
 *     with that participant's own snapshot. This is what keeps one participant's view
 *     from leaking the others (#4064) — including the first participant, whose QR is no
 *     longer overloaded with an all-participants doc.
 *   - Non-group single form: a single-element list.
 *   - Group/board entry (no subjectId): every participant's snapshot, in QR order, for
 *     the caller to stack into the combined view.
 *
 * With `capture`, completed QRs without a snapshot get one first (ensureFormSnapshots); if
 * that capture can't run or fails, the result is [] so the caller opens the live form.
 *
 * @param {{ taskId: string, subjectId?: string|null, capture?: boolean }} context
 * @returns {Promise<string[]>} DocumentReference ids, or [] when no snapshot exists
 */
export async function resolveSnapshotDocRefs({ taskId, subjectId = null, capture = false }) {
    if (!taskId) return [];

    // 1. Fetch the task. Could be either:
    //    - A child participant task (groupTask points up, focus + encounter set)
    //    - A parent group task (no focus, no encounter, basedOn=[appointment])
    const task = unwrapSingle(await $httpFhirApi.get(`Task/${taskId}`).catch(() => null));
    if (!task) return [];

    // 2. Gather the group's QRs (see fetchGroupQrs), seeded with the task's own
    //    focus so a child participant task still resolves even if the group query
    //    misses it.
    const parentId = task.groupTask || task.id;
    const qrs = await fetchGroupQrs(parentId, task.focus?.id ? [task.focus.id] : []);
    if (!qrs.length) return [];

    // 3. Keep the locked QRs that carry a snapshot pointer, capturing missing ones first when asked.
    const completedQrs = qrs.filter((qr) => qr?.status === 'completed');
    let snapshotIds = completedQrs.map((qr) => qr?.formPdfSnapshot?.id);
    if (capture && completedQrs.length) {
        snapshotIds = await ensureFormSnapshots(completedQrs, { groupNote: !!task.groupTask });
        if (!snapshotIds) return [];
    }
    const validQrs = completedQrs.map((qr, index) => ({ qr, snapshotId: snapshotIds[index] })).filter(({ snapshotId }) => snapshotId);
    if (!validQrs.length) return [];

    // Participant-chart / activity-log entry: that participant's own snapshot only.
    if (subjectId) {
        const participant = validQrs.find(({ qr }) => qr?.subject?.id === subjectId);
        return participant ? [participant.snapshotId] : [];
    }

    // No subject: a single form yields one doc; a group yields every participant's
    // snapshot, in QR order, for the caller to stack into the combined view.
    return validQrs.map(({ snapshotId }) => snapshotId);
}

/**
 * Which snapshot a section of a stacked document came from — see mergeSnapshotHtml. Read by the
 * snapshot print path, which names each section's own patient in its footer.
 */
export const SNAPSHOT_OWNER_ATTR = 'data-snapshot-owner';

/**
 * Combine several participant snapshots into one document for the group/board view.
 *
 * Group notes have no dedicated combined doc — each participant's snapshot is a self-contained
 * HTML document. We reuse the first document's shell (it carries the first section) and append
 * every other document's `.bn-snapshot-wrap` sections into it, separated by page breaks so the
 * print path (extractSnapshotBody) paginates them correctly.
 *
 * Sections go into `.bn-snapshot-doc`, the shell's container. This used to be Vuetify's
 * `.v-application__wrap` and the choice was load-bearing: `.v-application` on the body is
 * display:flex (row), so sections appended straight to <body> laid out side by side rather than
 * stacking. The document loads no Vuetify now, so that hazard is gone and the <body> fallback
 * below stacks correctly too — which is also what lets a snapshot captured before the shell
 * changed still merge.
 *
 * Each participant's section is their snapshot whole — the letterhead, the title and the group
 * tab's authored narrative included — so the stack reads as it prints.
 *
 * `owners` says which snapshot each document is — an index the caller can resolve, such as its
 * position in the DocumentReference list. Each section is stamped with it (SNAPSHOT_OWNER_ATTR),
 * because printing the combined document has to name each participant on their own pages and
 * the sections carry nothing else that says whose they are.
 *
 * @param {string[]} htmls one captured document per participant, in display order
 * @param {Array<number|null>} [owners] the owner of each entry in `htmls`, by position
 * @returns {string} a single document, or '' when there is nothing to combine
 */
export function mergeSnapshotHtml(htmls, owners = null) {
    const valid = [];
    const validOwners = [];
    (htmls || []).forEach((html, index) => {
        if (!html) return;
        valid.push(html);
        validOwners.push(owners ? owners[index] : null);
    });
    // A lone document is returned exactly as it was captured. Parsing and re-serialising it
    // would rewrite stored markup for no gain, and there is nothing to combine or to repeat.
    if (valid.length <= 1) return valid[0] || '';

    const stamp = (section, owner) => {
        if (owner !== null && owner !== undefined && section.classList?.contains('bn-snapshot-wrap')) section.setAttribute(SNAPSHOT_OWNER_ATTR, String(owner));
    };

    const parser = new DOMParser();
    const base = parser.parseFromString(valid[0], 'text/html');
    const container = base.querySelector('.bn-snapshot-doc') || base.querySelector('.v-application__wrap') || base.body;
    base.querySelectorAll('.bn-snapshot-wrap').forEach((wrap) => stamp(wrap, validOwners[0]));

    valid.slice(1).forEach((html, index) => {
        const doc = parser.parseFromString(html, 'text/html');
        const wraps = Array.from(doc.querySelectorAll('.bn-snapshot-wrap'));
        // Fallback: a snapshot without a wrap contributes its whole body.
        const sections = wraps.length ? wraps : doc.body ? [doc.body] : [];
        sections.forEach((section) => {
            const separator = base.createElement('div');
            separator.className = 'page-break';
            container.appendChild(separator);
            const imported = base.importNode(section, true);
            stamp(imported, validOwners[index + 1]);
            container.appendChild(imported);
        });
    });

    return '<!DOCTYPE html>' + base.documentElement.outerHTML;
}
